import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import sessionStorage from "./utils/sessionStore";
import { Container, Row, Col, Card, Badge, Spinner, Alert, Button } from "react-bootstrap";
import { FaEyeSlash } from "react-icons/fa";
import "./ProductBOM.css";

const API_URL = process.env.REACT_APP_API_URL;

function ProductBOM() {
  const { partNum } = useParams();
  const navigate = useNavigate();
  const [bomData, setBomData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoggedIn = !!sessionStorage.getItem('authToken');

  // Helper: show price or eye icon
  const PriceOrHidden = ({ value }) => {
    if (isLoggedIn) return <>{formatCurrency(value)}</>;
    return <FaEyeSlash style={{ color: "#999", fontSize: "16px" }} title="Login to view price" />;
  };

  useEffect(() => {
    const fetchBomData = async () => {
      if (!partNum) {
        setError("No part number provided");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_URL}/api/Part/${partNum}`);

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(`No BOM data found for part number "${partNum}"`);
          }
          throw new Error(`Server error: ${response.status}`);
        }

        const data = await response.json();
        setBomData(data);
      } catch (err) {
        console.error("Error fetching BOM data:", err);
        if (err.message.includes("Failed to fetch")) {
          setError("Unable to connect to server. Please check if the API is running.");
        } else {
          setError(err.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchBomData();
  }, [partNum]);

  // Group BOM data by BomType
  const groupedData = bomData.reduce((acc, item) => {
    const type = item.bomType || "Other";
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(item);
    return acc;
  }, {});

  const getBomTypeBadgeColor = (bomType) => {
    switch (bomType) {
      case "Main BOM":
        return "primary";
      case "Sterile Pack":
        return "success";
      case "Special Order":
        return "warning";
      default:
        return "secondary";
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  return (
    <div style={{ height: "100vh", overflowY: "auto", paddingTop: "80px", backgroundColor: "#dce8f3" }}>
      <Container fluid className="product-bom-container">
        {/* Page Header */}
        <div className="bom-header">
          <div className="header-content">
            <h1>Product Kit Components</h1>
            <h2 className="part-number">{partNum}</h2>
          </div>
        </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-container">
          <Spinner animation="border" variant="primary" />
          <p>Loading BOM data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Alert variant="danger" className="error-alert">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Alert>
      )}

      {/* BOM Data */}
      {!isLoading && !error && bomData.length > 0 && (
        <div className="bom-content">
          {/* Summary Stats */}
          <Row className="summary-row">
            <Col md={3} sm={6}>
              <Card className="summary-card">
                <Card.Body>
                  <div className="summary-value">{bomData.length}</div>
                  <div className="summary-label">Total Components</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="summary-card">
                <Card.Body>
                  <div className="summary-value">
                    {Object.keys(groupedData).length}
                  </div>
                  <div className="summary-label">BOM Types</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="summary-card">
                <Card.Body>
                  <div className="summary-value">
                    {isLoggedIn ? formatCurrency(
                      bomData.reduce((sum, item) => sum + (item.extendedUnitPrice || 0), 0)
                    ) : <FaEyeSlash style={{ color: "#999" }} title="Login to view price" />}
                  </div>
                  <div className="summary-label">Total Extended Price</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="summary-card">
                <Card.Body>
                  <div className="summary-value">
                    {bomData.reduce((sum, item) => sum + (item.qtyPer || 0), 0)}
                  </div>
                  <div className="summary-label">Total Quantity</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* BOM Groups */}
          {Object.entries(groupedData).map(([bomType, items]) => (
            <div key={bomType} className="bom-group">
              <h3 className="group-title">
                <Badge bg={getBomTypeBadgeColor(bomType)}>{bomType}</Badge>
                <span className="item-count">({items.length} items)</span>
              </h3>
              <Row>
                {items.map((item, index) => (
                  <Col key={`${item.mtlPartNum}-${index}`} lg={3} md={4} sm={6} xs={12}>
                    <Card className="bom-card">
                      <Card.Header className="bom-card-header">
                        <span className="mtl-part-num">{item.mtlPartNum}</span>
                        {item.revisionNum && (
                          <Badge bg="info" className="revision-badge">
                            Rev {item.revisionNum}
                          </Badge>
                        )}
                      </Card.Header>
                      <Card.Body>
                        <div className="bom-detail">
                          <span className="detail-label">Quantity:</span>
                          <span className="detail-value">{item.qtyPer}</span>
                        </div>
                        <div className="bom-detail">
                          <span className="detail-label">Unit Price:</span>
                          <span className="detail-value">
                            <PriceOrHidden value={item.unitPrice} />
                          </span>
                        </div>
                        {item.pricePerCode && (
                          <div className="bom-detail">
                            <span className="detail-label">Price Per:</span>
                            <span className="detail-value">{item.pricePerCode}</span>
                          </div>
                        )}
                        {item.fullSystemDiscount > 0 && (
                          <div className="bom-detail discount">
                            <span className="detail-label">Discount:</span>
                            <span className="detail-value">
                              {isLoggedIn ? `${item.fullSystemDiscount}%` : <FaEyeSlash style={{ color: "#999", fontSize: "16px" }} title="Login to view" />}
                            </span>
                          </div>
                        )}
                        <hr />
                        <div className="bom-detail total">
                          <span className="detail-label">Extended Price:</span>
                          <span className="detail-value">
                            <PriceOrHidden value={item.extendedUnitPrice} />
                          </span>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </div>
      )}

      {/* No Data State */}
      {!isLoading && !error && bomData.length === 0 && (
        <Alert variant="info" className="no-data-alert">
          <Alert.Heading>No Data Found</Alert.Heading>
          <p>No BOM components found for part number "{partNum}".</p>
          <Button variant="outline-info" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Alert>
      )}
      </Container>
    </div>
  );
}

export default ProductBOM;
